/**
 * Cliente HTTP da API da aplicacao (api/, FastAPI na 8000).
 *
 * O navegador NAO fala com o gateway. O gateway e um processo unico de
 * tempo real, com estado em memoria e latencia critica; a API e stateless
 * e escala na horizontal. A fronteira entre os dois e o Postgres.
 *
 * Toda chamada leva o JWT do Supabase no header, quando ha sessao. A API
 * confere a assinatura com a chave publica do projeto (ES256 via JWKS) e
 * resolve o usuario em public.users.
 *
 * Os tipos de resposta vem do OpenAPI: @shared/types/api.
 */
import { tokenAtual } from '@shared/auth/supabase';

// Vazio por padrao: as chamadas saem como caminho relativo (/api/...) e
// quem resolve e o proxy do Vite no dev, ou a mesma origem em producao.
// Assim a porta da API nao fica gravada no bundle.
// Defina VITE_API_URL so para apontar para outro host.
const BASE = import.meta.env.VITE_API_URL ?? '';

/** O endereço completo de uma rota da API, para quem precisa chamar
 *  `fetch` direto (download de arquivo, por exemplo) em vez de `api.get`.
 *  Sem isto, o caminho relativo funciona no dev (o proxy do Vite resolve)
 *  e quebra em silêncio no dia em que a API tiver domínio próprio: o
 *  pedido vai para o site, que responde 200 com o index.html. */
export function urlApi(rota: string): string {
  return `${BASE}${rota}`;
}

/** O texto que a API mandou, tirado do envelope do FastAPI.
 *
 *  Duas formas, as duas com a chave `detail`:
 *    HTTPException      {"detail": "CPF invalido"}
 *    erro de validacao  {"detail": [{"loc": [...], "msg": "Field required"}]}
 *
 *  A segunda e do proprio framework e cita o nome do campo em ingles
 *  ("body", "email"), entao so aproveitamos as mensagens e juntamos. Nao
 *  vale traduzir `loc` aqui: o formulario ja rotula os campos, e errar a
 *  traducao seria pior que omitir.
 */
function textoDoErro(corpo: string): string | null {
  if (!corpo.trim()) return null;
  try {
    const { detail } = JSON.parse(corpo) as { detail?: unknown };
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d as { msg?: unknown }).msg)
        .filter((m): m is string => typeof m === 'string' && m.trim() !== '');
      if (msgs.length > 0) return msgs.join('; ');
    }
    return null;
  } catch {
    // Nao era JSON: um 502 do proxy, um HTML de gateway. O corpo cru nao
    // ajuda o usuario, entao quem chama cai na mensagem por status.
    return null;
  }
}

/** O que dizer quando a API nao explicou. Por status, porque e a unica
 *  coisa que se sabe -- e "API 500: " na tela nao e frase. */
function porStatus(status: number): string {
  if (status === 401) return 'Sua sessão expirou. Entre de novo.';
  if (status === 403) return 'Você não tem permissão para esta ação.';
  if (status === 404) return 'Não encontrado.';
  if (status === 409) return 'Esta ação conflita com o estado atual.';
  if (status === 422) return 'Há campos inválidos no formulário.';
  if (status >= 500) return 'A API falhou. Tente de novo em instantes.';
  return 'Não foi possível completar a ação.';
}

export class ErroApi extends Error {
  readonly status: number;
  readonly corpo: string;

  constructor(status: number, corpo: string) {
    // `message` e o que as telas mostram -- todas renderizam
    // `error.message` direto. Por isso ele e a frase para o usuario, e
    // nao o envelope HTTP: "API 422: {"detail":"CPF invalido"}" vazava
    // JSON cru para dentro do formulario. O status e o corpo continuam
    // disponiveis nos campos, para quem precisar decidir por eles.
    super(textoDoErro(corpo) ?? porStatus(status));
    this.name = 'ErroApi';
    this.status = status;
    this.corpo = corpo;
  }

  /** Sem sessao valida: o app deve mandar para a tela de login. */
  get precisaLogin(): boolean {
    return this.status === 401;
  }

  /** Autenticado, mas sem permissao para este recurso. */
  get semPermissao(): boolean {
    return this.status === 403;
  }
}

async function pedir<T>(rota: string, init?: RequestInit): Promise<T> {
  const token = await tokenAtual();
  const r = await fetch(`${BASE}${rota}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
    credentials: 'include',
  });
  if (!r.ok) throw new ErroApi(r.status, await r.text().catch(() => ''));
  // 204 nao tem corpo -- chamar json() num corpo vazio lanca SyntaxError,
  // e um DELETE bem-sucedido apareceria como falha.
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

export const api = {
  get: <T>(rota: string) => pedir<T>(rota),
  post: <T>(rota: string, corpo?: unknown) =>
    pedir<T>(rota, { method: 'POST', body: JSON.stringify(corpo ?? {}) }),
  put: <T>(rota: string, corpo?: unknown) =>
    pedir<T>(rota, { method: 'PUT', body: JSON.stringify(corpo ?? {}) }),
  patch: <T>(rota: string, corpo?: unknown) =>
    pedir<T>(rota, { method: 'PATCH', body: JSON.stringify(corpo ?? {}) }),
  del: <T>(rota: string) => pedir<T>(rota, { method: 'DELETE' }),
};

/** Os assinantes do proprio gestor, ou os de um gestor lidos pelo admin. */
function baseAssinantes(gestor?: string | null) {
  return gestor ? `/api/admin/gestores/${encodeURIComponent(gestor)}/assinantes` : '/api/eu/assinantes';
}

/** Rotas em um lugar so: o dia de renomear nao vira cacada.
 *  Implementadas: eu, bridges, operacao, auditoria. */
export const rotas = {
  eu: '/api/auth/me',
  meuCadastro: '/api/eu/cadastro',
  onboarding: '/api/eu/onboarding',
  estrategias: '/api/eu/estrategias',
  estrategiaPrevia: '/api/eu/estrategias/previa',
  estrategiaItem: (id: number) => `/api/eu/estrategias/${id}`,
  portfolios: '/api/eu/portfolios',
  portfolioPrevia: '/api/eu/portfolios/previa',
  portfolioItem: (id: number) => `/api/eu/portfolios/${id}`,
  portfolioRecalcular: (id: number) => `/api/eu/portfolios/${id}/recalcular`,
  portfolioCorrelacao: (id: number) => `/api/eu/portfolios/${id}/correlacao`,
  portfolioPublicar: (id: number) => `/api/eu/portfolios/${id}/publicar`,
  portfolioDespublicar: (id: number) => `/api/eu/portfolios/${id}/despublicar`,
  // PUBLICA: sem `/eu`, sem token. Quem chama ainda nao tem conta --
  // e esta chamada que a cria.
  cadastroCliente: '/api/cadastro',
  cadastroReenviar: '/api/cadastro/reenviar',
  // A conta de negociacao do CLIENTE (slave). GET le o estado do
  // provisionamento; POST envia as credenciais e dispara o trabalho.
  minhaConta: '/api/eu/conta-negociacao',
  // A conta que assina UM portfolio: o cliente pode ter varias, e a tela
  // de configuracao acompanha a daquele portfolio.
  contaDoPortfolio: (gestor: string, slug: string) =>
    `/api/eu/conta-negociacao?gestor=${encodeURIComponent(gestor)}&slug=${encodeURIComponent(slug)}`,
  // A home do cliente, agregada: conta, estado vivo, portfolio, ordens.
  // `conta` escolhe qual (o cliente pode ter varias); sem ela, a mais
  // recente.
  home: (conta?: string) =>
    conta ? `/api/eu/home?conta=${encodeURIComponent(conta)}` : '/api/eu/home',
  // A home do gestor: as contas master dele, agora, e o que operaram.
  // Clientes, PL e receita vem de `assinantes`, do mesmo cache.
  homeGestor: '/api/eu/home-gestor',
  // Todas as contas de negociacao do cliente (pode ter mais de uma).
  minhasContas: '/api/eu/contas-negociacao',
  // O perfil do cliente: dados pessoais (GET/PUT) e as assinaturas, com o
  // registro do que foi feito com cada uma.
  meuPerfil: '/api/eu/perfil',
  minhasAssinaturas: '/api/eu/assinaturas',
  // O sino: os avisos de quem esta logado, e marcar como lidos.
  notificacoes: '/api/eu/notificacoes',
  notificacoesLidas: '/api/eu/notificacoes/lidas',
  // O cliente responde ao pedido de pausa do gestor.
  pausaDoGestor: (conta: string, resposta: 'aprovar' | 'recusar') =>
    `/api/eu/contas-negociacao/${encodeURIComponent(conta)}/pausa-do-gestor/${resposta}`,
  // A corretora exige trocar a senha da conta: o cliente escolhe a nova e
  // autoriza a plataforma a trocar por ele.
  trocarSenhaConta: (conta: string) =>
    `/api/eu/contas-negociacao/${encodeURIComponent(conta)}/trocar-senha`,
  // O cliente ja trocou a senha na corretora e informa a nova.
  senhaConta: (conta: string) =>
    `/api/eu/contas-negociacao/${encodeURIComponent(conta)}/senha`,
  // A conta com falha (ex.: netting convertida para hedging) tenta de novo.
  reconectarConta: (conta: string) =>
    `/api/eu/contas-negociacao/${encodeURIComponent(conta)}/reconectar`,
  // O gestor age sobre a assinatura de um cliente.
  assinanteAcao: (id: number,
                  acao: 'pedir-pausa' | 'retirar-pedido' | 'retomar' | 'cancelar') =>
    `/api/eu/assinantes/${id}/${acao}`,
  // Pausar, retomar e cancelar a assinatura de uma conta do cliente.
  assinaturaAcao: (id: string, acao: 'pausar' | 'retomar' | 'cancelar') =>
    `/api/eu/contas-negociacao/${encodeURIComponent(id)}/${acao}`,
  // As posicoes do copy na conta do cliente, com filtros na query string
  // (conta, estado, sinal, ativo, lado, de, ate, ordem, pagina).
  posicoes: (qs: string) => `/api/eu/posicoes${qs ? `?${qs}` : ''}`,
  // As mesmas posicoes, na conta master do gestor (mesmos filtros).
  posicoesMaster: (qs: string) => `/api/eu/posicoes-master${qs ? `?${qs}` : ''}`,
  // Track record: a ficha calculada dos deals reais da conta.
  // O que o cliente paga ao gestor numa conta: plano, mes em curso com
  // projecao, marca d'agua e extrato mensal. Uma ESTIMATIVA -- quem cobra
  // e a corretora.
  custos: (conta?: string) =>
    conta ? `/api/eu/custos?conta=${encodeURIComponent(conta)}` : '/api/eu/custos',
  meuHistorico: (escopo: 'copy' | 'tudo', conta?: string) =>
    `/api/eu/historico?escopo=${escopo}${conta ? `&conta=${encodeURIComponent(conta)}` : ''}`,
  historicoConta: (id: string, escopo: 'copy' | 'tudo') =>
    `/api/admin/contas/${encodeURIComponent(id)}/historico?escopo=${escopo}`,
  /** As posicoes, abertas e fechadas, de qualquer conta da frota (admin). */
  posicoesConta: (id: string, qs: string) =>
    `/api/admin/contas/${encodeURIComponent(id)}/posicoes${qs ? `?${qs}` : ''}`,
  // O gestor lendo a propria conta master. Sem `escopo`: no master os
  // dois sao iguais -- o EA ja filtra pelo magic permitido.
  historicoMinhaMaster: (id: string) =>
    `/api/eu/contas-master/${encodeURIComponent(id)}/historico`,
  // Os clientes que seguem as contas master do gestor, com o extrato
  // de cada um. `conta` recorta os totais para uma conta master.
  // `gestor` e o admin lendo a tela de um gestor: mesma resposta, sob
  // /api/admin/gestores/{id}.
  assinantes: (conta?: string, gestor?: string | null) =>
    `${baseAssinantes(gestor)}${conta ? `?conta=${encodeURIComponent(conta)}` : ''}`,
  // So o escopo copy: o gestor nao ve os trades manuais do cliente.
  historicoAssinante: (id: number, gestor?: string | null) =>
    `${baseAssinantes(gestor)}/${id}/historico`,
  replicacaoAssinante: (id: number, qs: string, gestor?: string | null) =>
    `${baseAssinantes(gestor)}/${id}/replicacao${qs ? `?${qs}` : ''}`,
  // Reclassificar um lancamento. O gestor PROPOE (vira pedido pendente);
  // o admin APLICA direto. A fila e a decisao sao so do admin.
  ajusteMaster: (id: string, ticket: number) =>
    `/api/eu/contas-master/${encodeURIComponent(id)}/movimentacoes/${ticket}/ajuste`,
  ajusteAdmin: (id: string, ticket: number) =>
    `/api/admin/contas/${encodeURIComponent(id)}/movimentacoes/${ticket}/ajuste`,
  filaAjustes: (status: 'pendente' | 'aprovado' | 'recusado' | 'todos') =>
    status === 'todos'
      ? '/api/admin/movimentacoes/ajustes?todos=true'
      : `/api/admin/movimentacoes/ajustes?status=${status}`,
  decisaoAjuste: (id: number) => `/api/admin/movimentacoes/ajustes/${id}/decisao`,
  // Usuarios cadastrados, com papeis e contas. Busca e filtros no servidor.
  usuariosAdmin: (f: { busca: string; papel: string; conta: string; pagina: number; porPagina: number }) => {
    const q = new URLSearchParams({ papel: f.papel, conta: f.conta,
                                    pagina: String(f.pagina), por_pagina: String(f.porPagina) });
    if (f.busca.trim()) q.set('busca', f.busca.trim());
    return `/api/admin/usuarios?${q}`;
  },
  // PUBLICA: sem `/eu`, sem token. A lamina que o cliente ve.
  laminaPublica: (gestor: string, slug: string) =>
    `/api/publico/portfolios/${encodeURIComponent(gestor)}/${encodeURIComponent(slug)}`,
  cobranca: '/api/eu/cobranca',
  cobrancaItem: (id: number) => `/api/eu/cobranca/${id}`,
  cobrancaDocumento: (id: number) => `/api/eu/cobranca/${id}/documento`,
  cobrancaUploadUrl: (nome: string) =>
    `/api/eu/cobranca/upload-url?nome_arquivo=${encodeURIComponent(nome)}`,
  contasMaster: '/api/eu/contas-master',
  contaMasterItem: (id: string) => `/api/eu/contas-master/${encodeURIComponent(id)}`,
  contaMasterPacote: (id: string) =>
    `/api/eu/contas-master/${encodeURIComponent(id)}/pacote`,
  contaMasterInstalador: '/api/eu/contas-master/instalador',
  contaMasterDivulgacao: (id: string) =>
    `/api/eu/contas-master/${encodeURIComponent(id)}/divulgacao`,
  contaMasterRetirarDivulgacao: (id: string) =>
    `/api/eu/contas-master/${encodeURIComponent(id)}/divulgacao/retirar`,
  provedores: '/api/admin/provedores',
  provedorConvite: (id: string) => `/api/admin/provedores/${id}/reenviar-convite`,
  provedorDecisao: (id: string) => `/api/admin/provedores/${id}/decisao`,
  provedorLink: (id: string) => `/api/admin/provedores/${id}/link`,
  provedorTermos: (id: string) => `/api/admin/provedores/${id}/termos`,
  adminContasMaster: '/api/admin/contas-master',
  // A saude da replicacao de todas as masters, em totais. Sem `ate`, agora.
  adminSaudeReplicacao: (de: string, ate?: string) => {
    const q = new URLSearchParams({ de });
    if (ate) q.set('ate', ate);
    return `/api/admin/replicacao?${q}`;
  },
  adminCorretoras: '/api/admin/corretoras',
  adminCorretora: (key: string) => `/api/admin/corretoras/${encodeURIComponent(key)}`,
  adminCorretoraArquivo: (key: string, tipo: string) =>
    `/api/admin/corretoras/${encodeURIComponent(key)}/arquivos/${tipo}`,
  adminCorretoraAtivar: (key: string) =>
    `/api/admin/corretoras/${encodeURIComponent(key)}/ativar`,
  adminCorretoraDesativar: (key: string) =>
    `/api/admin/corretoras/${encodeURIComponent(key)}/desativar`,
  adminContaDecisao: (id: string) =>
    `/api/admin/contas-master/${encodeURIComponent(id)}/decisao`,
  adminContaDocumento: (id: string) =>
    `/api/admin/contas-master/${encodeURIComponent(id)}/documento`,
  bridges: '/api/admin/bridges',
  operacao: '/api/admin/operacao',
  auditoria: '/api/admin/auditoria',
  // A auditoria paginada: indicadores e uma rota por lista.
  auditoriaResumo: '/api/admin/auditoria/resumo',
  auditoriaLista: (lista: 'ordens' | 'conexoes' | 'acessos', pagina: number, porPagina: number) =>
    `/api/admin/auditoria/${lista}?pagina=${pagina}&por_pagina=${porPagina}`,
  // Hosts (VPS) e seus agentes. Comandos viajam pelo /fleet/desired do
  // gateway: a API so enfileira; quem executa e o agente, no proximo
  // ciclo. `comandoHost` e o que a tela consulta ate o resultado chegar.
  hosts: '/api/admin/hosts',
  hostsLista: (comBridges: boolean) =>
    comBridges ? '/api/admin/hosts' : '/api/admin/hosts?bridges=false',
  hostDetalhe: (host: string) => `/api/admin/hosts/${encodeURIComponent(host)}`,
  // Novo host: POST em `hosts` cria e devolve o comando de instalacao;
  // `hostMatricula` gera outro comando para um host ainda pendente.
  hostMatricula: (host: string) => `/api/admin/hosts/${encodeURIComponent(host)}/matricula`,
  hostComandos: (host: string) => `/api/admin/hosts/${encodeURIComponent(host)}/comandos`,
  hostDesired: (host: string, conta: string) =>
    `/api/admin/hosts/${encodeURIComponent(host)}/bridges/${encodeURIComponent(conta)}/desired`,
  // Mover: a conta passa para outro host; a origem recebe um `remove`.
  hostMover: (host: string, conta: string) =>
    `/api/admin/hosts/${encodeURIComponent(host)}/bridges/${encodeURIComponent(conta)}/mover`,
  comandoHost: (id: number) => `/api/admin/hosts/comandos/${id}`,
  contas: '/api/admin/contas',
  assinaturas: '/api/admin/assinaturas',
  ordens: '/api/admin/ordens',
  ordemTimeline: (id: string) => `/api/admin/ordens/${id}/timeline`,
  recusasPorMotivo: '/api/admin/metricas/recusas',
} as const;
